// Bajar UNA creación por su fila — nació como closure de `PanelTandaCreaciones` y salió a
// módulo cuando el lienzo del Agente necesitó exactamente lo mismo
// (ADR-2026-09-16-la-descarga-de-una-creacion-sale-a-modulo). Tres copias de la misma
// descarga ya había entre el inline y el historial; no hace falta una cuarta.

import { supabase } from '../../../lib/supabase';
import {
  OPCIONES_SIN_TOCAR,
  type OpcionesDeDescarga,
  type OpcionesDeLaPregunta,
} from '../../../components/descarga/useOpcionesDeDescarga';
import { descargarDesdeBlob } from '../../../utils/descargarArchivo';
import { buildDownloadFileName } from '../../../utils/downloadFileName';
import { marcarDescargadas } from '../../../utils/marcarDescargada';
import { TTL_FIRMA } from '../../../utils/firmarEnLote';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../../utils/imageCache';
import type { DatosCelda } from './useDatosDeCeldas';

export interface ParaDescargar {
  companyId: string;
  datos: DatosCelda;
  esVideo: boolean;
  resolverOpciones: (opts?: OpcionesDeLaPregunta) => Promise<OpcionesDeDescarga | null>;
  aplicarOpciones: (
    blob: Blob,
    esVideo: boolean,
    opciones: OpcionesDeDescarga | null,
  ) => Promise<Blob>;
  /** Elección ya hecha (descarga de varias): no se vuelve a preguntar. */
  eleccion?: OpcionesDeDescarga | null;
}

/** `false` = el usuario canceló en el selector. Lanza un `Error` con mensaje mostrable. */
export async function descargarCreacion(p: ParaDescargar): Promise<boolean> {
  if (!p.datos.fileName) throw new Error('No se encontró el archivo original.');
  // Video sin pregunta: la marca de agua solo se compone sobre imágenes.
  const eleccion =
    p.eleccion !== undefined
      ? p.eleccion
      : p.esVideo
        ? OPCIONES_SIN_TOCAR
        : await p.resolverOpciones();
  if (eleccion === null) return false;
  const path = `${p.companyId}/full/${p.datos.fileName}`;
  let url = getCachedSignedUrl(path);
  if (!url) {
    const { data } = await supabase.storage.from('generadas').createSignedUrl(path, TTL_FIRMA);
    url = data?.signedUrl ?? null;
    if (url) setCachedSignedUrl(path, url);
  }
  if (!url) throw new Error('No se encontró el archivo original.');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo traer el archivo (HTTP ${res.status}).`);
  const blob = await p.aplicarOpciones(await res.blob(), p.esVideo, eleccion);
  const bajo = await descargarDesdeBlob({
    blob,
    fileName: buildDownloadFileName({
      variantName: p.datos.varianteNombre,
      originalFileName: p.datos.fileName,
      forceVideo: p.esVideo,
    }),
    esVideo: p.esVideo,
  });
  // Recién con el archivo entregado (o compartido, en iOS): cancelar la hoja no cuenta.
  // La marca vive ACÁ y no en cada pantalla porque las tres que bajan por este camino —la
  // columna de resultados, el lienzo del Agente y la barra del diseño— tienen que marcar
  // igual; el lienzo era justamente uno de los que no marcaba
  // (ADR-2026-09-17-la-descarga-de-v2-y-flujos-no-marca-descargada).
  if (bajo) void marcarDescargadas([p.datos.id], p.companyId);
  return bajo;
}
